import { useCallback } from 'react';
import { ApolloCache, ApolloError, FetchResult } from '@apollo/client';
import {
  useUpdateTimeSignalsByIdMutation,
  UpdateTimeSignalInput,
  UpdateTimeSignalsByIdMutation,
  TimeSignalClassification,
  TimeSignalAcceptedBy
} from 'graphql/types';

const UPDATE_TIME_SIGNALS_BY_ID_BATCH_LIMIT = 250;

interface UpdateTimeSignalsByIdInput {
  updates: UpdateTimeSignalInput[];
  awaitRefetchQueries?: boolean;
  refetchQueries?: string[];
  update?: (
    cache: ApolloCache<UpdateTimeSignalsByIdMutation>,
    mutationResult: FetchResult<UpdateTimeSignalsByIdMutation>
  ) => void;
}

function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const result: T[][] = [];

  for (let i = 0; i < array.length; i += chunkSize) {
    const chunk = array.slice(i, i + chunkSize);
    result.push(chunk);
  }

  return result;
}

export const useUpdateTimeSignalsById = (): {
  updateTimeSignalsById: (input: UpdateTimeSignalsByIdInput) => Promise<void[]>;
  loading: boolean;
  error: ApolloError | undefined;
} => {
  const [
    updateTimeSignalsByIdMutation,
    { loading, error }
  ] = useUpdateTimeSignalsByIdMutation();
  const updateTimeSignalsById = useCallback(
    async ({
      updates,
      awaitRefetchQueries = true,
      refetchQueries = ['timeSignals', 'timeSignalsCount', 'timeSignalsTotals'],
      update = undefined
    }: UpdateTimeSignalsByIdInput) => {
      const finalUpdates = updates.map((update) => {
        return {
          ...update,
          acceptedBy:
            update.classification === TimeSignalClassification.Accepted
              ? TimeSignalAcceptedBy.User
              : undefined
        };
      });
      const batches: UpdateTimeSignalInput[][] = chunkArray(
        finalUpdates,
        UPDATE_TIME_SIGNALS_BY_ID_BATCH_LIMIT
      );
      const updatePromises = batches.map(
        async (batch: UpdateTimeSignalInput[]) => {
          await updateTimeSignalsByIdMutation({
            variables: {
              input: batch
            },
            awaitRefetchQueries: awaitRefetchQueries,
            refetchQueries: refetchQueries,
            update: update
          });
        }
      );

      return Promise.all(updatePromises);
    },
    [updateTimeSignalsByIdMutation]
  );

  return {
    updateTimeSignalsById,
    loading: loading,
    error: error
  };
};
